/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketChrome as ticketUi } from '../../shared/ticket/ticketChrome.styles';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { CatalogProductFormActions } from './CatalogProductFormActions';
import { CatalogProductFormFields } from './CatalogProductFormFields';
import type { CategoryOption } from '../domain/categoryOptions';
import type { Product } from '../domain/productTypes';
import type { CatalogProductRow } from '../domain/catalogTypes';
import type { ProductDraft, ProductVariantDraft } from '../domain/productDraft';
import type { AxisInput } from '../domain/variantMatrix';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import type { CatalogProductFormMode } from '../store/useCatalogOverlayStore';
import type { ComboDraftComponent } from '../domain/productCombos';
import { catalogChromeCopy, catalogItemNoun } from '../domain/catalogTerminology';
import { normalizeUnitOfMeasure } from '@shared/quantity';
import { UOM_OPTIONS } from './catalogProductForm.styles';

type CatalogProductFormModalProps = {
  visible: boolean;
  /** Org con catálogo híbrido: naming "ítem" en lugar de "producto". */
  serviceCatalogEnabled?: boolean;
  editingProduct: CatalogProductRow | null;
  formMode: CatalogProductFormMode;
  formSuggestion: Extract<import('../domain/barcodeLookup').ProductLookupResult, { kind: 'remote_hit' }> | null;
  draft: ProductDraft;
  onNameChange: (value: string) => void;
  onBarcodeChange: (value: string) => void;
  onSkuChange: (value: string) => void;
  onPriceChange: (value: string) => void;
  onItemTypeChange: (itemType: ProductDraft['itemType']) => void;
  onCategoryChange: (category: Product['categoryId']) => void;
  categoryOptions: CategoryOption[];
  priceMarginPercentInput: string;
  onPriceMarginPercentChange: (value: string) => void;
  codeValidationMessage: string | null;
  barcodeValidationMessage: string | null;
  priceValidationMessage: string | null;
  draftValidationMessage: string | null;
  isDraftReady: boolean;
  saving: boolean;
  currentBranchName: string;
  currentBranchId: string | null;
  editingProductStockLoading: boolean;
  /** La lectura de stock/costo falló — nunca significa "sin stock". */
  editingProductStockFailed?: boolean;
  /** Reintenta la lectura de stock/costo (también tras guardar un costo). */
  onRetryEditingProductStock?: () => void;
  onComboCompositionSaved?: () => void;
  comboDraftComponents?: ComboDraftComponent[];
  onComboDraftChange?: (next: ComboDraftComponent[]) => void;
  comboPickOrganizationId?: string | null;
  onRecipeSaved?: () => void;
  onRecipeProduce?: () => void;
  onRecipeDisassemble?: () => void;
  editingProductCost: number | null;
  /** Permiso `Catalog.edit_cost`: permite cargar el costo desde el form. */
  canEditCost?: boolean;
  editingProductCurrentMarginValue: number | null;
  editingProductCurrentMarginPercent: number | null;
  editingProductSuggestedPrice: number | null;
  currencyFormatter: { format: (value: number) => string };
  onClose: () => void;
  onSave: () => void;
  onSaveAndContinue?: () => void;
  showSaveAndContinue?: boolean;
  onSaveAttemptWhenInvalid?: (message: string) => void;
  onOpenScanner: () => void;
  onApplySuggestedPrice: () => void;
  showBarcodeCameraButton?: boolean;
  onBarcodeSubmitEditing?: () => void;
  autoFocusBarcodeOnWeb?: boolean;
  canCreateCategory?: boolean;
  onCreateCategory?: () => void;
  canCreateBrand?: boolean;
  onCreateBrand?: () => void;
  /** Crea la marca desde el picker sheet y la selecciona en el draft. */
  onCreateBrandInline?: (name: string) => Promise<void>;
  canCreateSupplier?: boolean;
  onCreateSupplier?: () => void;
  /** Crea el proveedor desde el picker sheet y lo selecciona en el draft. */
  onCreateSupplierInline?: (name: string) => Promise<void>;
  supplierOptions?: Array<{ id: string; name: string }>;
  brandOptions?: Array<{ id: string; name: string }>;
  onBrandIdChange?: (value: string) => void;
  onInternalNotesChange?: (value: string) => void;
  onPublicDescriptionChange?: (value: string) => void;
  onMinOrderQtyChange?: (value: string) => void;
  onMaxOrderQtyChange?: (value: string) => void;
  onIsPublicChange?: (value: boolean) => void;
  onMenuAvailableChange?: (value: boolean) => void;
  onVisibleOnMenuCartaChange?: (value: boolean) => void;
  onActiveIngredientChange?: (value: string) => void;
  onUsageTagsChange?: (value: string) => void;
  onAllergensChange?: (value: string[]) => void;
  /** Capability `product_pharmacy_fields`: campos de farmacia (sin fork por rubro). */
  pharmacyFieldsEnabled?: boolean;
  onPreferredSupplierIdChange?: (value: string) => void;
  onSupplierItemCodeChange?: (value: string) => void;
  allowIvaRate?: boolean;
  weightUomEnabled?: boolean;
  onIvaRateChange?: (value: ProductDraft['ivaRate']) => void;
  onUnitOfMeasureChange?: (value: ProductDraft['unitOfMeasure']) => void;
  onDurationMinutesChange?: (value: string) => void;
  onBufferBeforeMinutesChange?: (value: string) => void;
  onBufferAfterMinutesChange?: (value: string) => void;
  onBookingModeChange?: (value: ProductDraft['bookingMode']) => void;
  onBookingPaymentModeChange?: (value: ProductDraft['bookingPaymentMode']) => void;
  onBookingDepositAmountChange?: (value: string) => void;
  onBookingDepositPercentChange?: (value: string) => void;
  onEvaluationProductIdChange?: (value: string) => void;
  serviceProductOptions?: Array<{ id: string; name: string; bookingMode?: string }>;
  /** Estado de la lectura del picker: 'error' significa falló, no "sin opciones". */
  serviceProductOptionsStatus?: 'idle' | 'loading' | 'ready' | 'error';
  onRetryServiceProductOptions?: () => void;
  onKitchenDestinationChange?: (value: ProductDraft['kitchenDestination']) => void;
  onPrepMinutesChange?: (value: string) => void;
  kitchenTicketsEnabled?: boolean;
  menuDietaryFiltersEnabled?: boolean;
  hideStockPanels?: boolean;
  variantsEnabled?: boolean;
  variantDrafts?: ProductVariantDraft[];
  parentStockQty?: number | null;
  onAddVariantDraft?: () => void;
  onUpdateVariantDraft?: (key: string, patch: Partial<ProductVariantDraft>) => void;
  onRemoveVariantDraft?: (key: string) => void;
  onGenerateVariantMatrix?: (axes: AxisInput[]) => void;
  onPickVariantImage?: (variantKey: string) => void;
  onRemoveVariantImage?: (variantKey: string) => void;
  variantImageUploadingKey?: string | null;
  onPickAxisImage?: (axis: string, value: string) => void;
  onRemoveAxisImage?: (axis: string, value: string) => void;
  axisImageUploading?: string | null;
  // #705: variantes inactivas para reactivación
  inactiveVariants?: Array<{ id: string; nameSuffix?: string | null; attributes?: Record<string, string> | null; sku?: string | null; barcode?: string | null }>;
  onReactivateVariant?: (variantId: string) => void;
  layoutWide?: boolean;
  imageUrl?: string;
  onPickImage?: () => void;
  onRemoveImage?: () => void;
  imageUploading?: boolean;
  videoUrl?: string | null;
  onPickVideo?: () => void;
  onRemoveVideo?: () => void;
  videoUploading?: boolean;
  formCompact?: boolean;
  onExpandForm?: () => void;
  /**
   * Identidad de la sesión de edición: cambia en cada apertura y tras
   * "guardar y seguir". El modal la usa para resetear el baseline de cambios
   * sin guardar y el estado de validación (touched/save-attempted).
   */
  formSession?: number;
};

export const CatalogProductFormModal = ({
  visible,
  serviceCatalogEnabled = false,
  editingProduct,
  formMode,
  formSuggestion,
  draft,
  onNameChange,
  onBarcodeChange,
  onSkuChange,
  onPriceChange,
  onItemTypeChange,
  onCategoryChange,
  categoryOptions,
  priceMarginPercentInput,
  onPriceMarginPercentChange,
  codeValidationMessage,
  barcodeValidationMessage,
  priceValidationMessage,
  draftValidationMessage,
  isDraftReady,
  saving,
  currentBranchName,
  currentBranchId,
  editingProductStockLoading,
  editingProductStockFailed = false,
  onRetryEditingProductStock,
  onComboCompositionSaved,
  comboDraftComponents,
  onComboDraftChange,
  comboPickOrganizationId,
  onRecipeSaved,
  onRecipeProduce,
  onRecipeDisassemble,
  editingProductCost,
  canEditCost = false,
  editingProductCurrentMarginValue,
  editingProductCurrentMarginPercent,
  editingProductSuggestedPrice,
  currencyFormatter,
  onClose,
  onSave,
  onSaveAndContinue,
  showSaveAndContinue,
  onSaveAttemptWhenInvalid,
  onOpenScanner,
  onApplySuggestedPrice,
  showBarcodeCameraButton = true,
  onBarcodeSubmitEditing,
  autoFocusBarcodeOnWeb = false,
  canCreateCategory = false,
  onCreateCategory,
  canCreateBrand = false,
  onCreateBrand,
  onCreateBrandInline,
  canCreateSupplier = false,
  onCreateSupplier,
  onCreateSupplierInline,
  supplierOptions = [],
  brandOptions = [],
  onBrandIdChange,
  onInternalNotesChange,
  onPublicDescriptionChange,
  onMinOrderQtyChange,
  onMaxOrderQtyChange,
  onIsPublicChange,
  onMenuAvailableChange,
  onVisibleOnMenuCartaChange,
  onActiveIngredientChange,
  onUsageTagsChange,
  onAllergensChange,
  pharmacyFieldsEnabled = false,
  onPreferredSupplierIdChange,
  onSupplierItemCodeChange,
  allowIvaRate = false,
  weightUomEnabled = false,
  onIvaRateChange,
  onUnitOfMeasureChange,
  onDurationMinutesChange,
  onBufferBeforeMinutesChange,
  onBufferAfterMinutesChange,
  onBookingModeChange,
  onBookingPaymentModeChange,
  onBookingDepositAmountChange,
  onBookingDepositPercentChange,
  onEvaluationProductIdChange,
  onKitchenDestinationChange,
  onPrepMinutesChange,
  kitchenTicketsEnabled = false,
  menuDietaryFiltersEnabled = false,
  serviceProductOptions,
  serviceProductOptionsStatus = 'idle',
  onRetryServiceProductOptions,
  hideStockPanels = false,
  variantsEnabled = false,
  variantDrafts = [],
  parentStockQty = null,
  onAddVariantDraft,
  onUpdateVariantDraft,
  onRemoveVariantDraft,
  onGenerateVariantMatrix,
  onPickVariantImage,
  onRemoveVariantImage,
  variantImageUploadingKey,
  onPickAxisImage,
  onRemoveAxisImage,
  axisImageUploading,
  inactiveVariants,
  onReactivateVariant,
  layoutWide = false,
  imageUrl,
  onPickImage,
  onRemoveImage,
  imageUploading,
  videoUploading,
  onPickVideo,
  onRemoveVideo,
  videoUrl,
  formCompact = false,
  onExpandForm,
  formSession = 0,
}: CatalogProductFormModalProps) => {
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();
  const [categorySelectorVisible, setCategorySelectorVisible] = useState(false);
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [uomPickerOpen, setUomPickerOpen] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const scrollContentRef = useRef<View>(null);
  const nameInputRef = useRef<import('react-native').TextInput>(null);
  const barcodeInputRef = useRef<import('react-native').TextInput>(null);
  const { confirm: confirmStockLoss, ConfirmModal: StockLossConfirmModal } = useOperationalConfirm();
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();

  // ── Cambios sin guardar + validación al guardar ──
  // Baseline: snapshot del draft al abrir (y tras "guardar y seguir" — el VM
  // sube `formSession`). Cualquier divergencia draft/variants = dirty.
  const [baselineKey, setBaselineKey] = useState<string>('');
  const [touchedFields, setTouchedFields] = useState<Record<string, true>>({});
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setBaselineKey(JSON.stringify({ draft, variantDrafts }));
    setTouchedFields({});
    setSaveAttempted(false);
    setDiscardConfirmOpen(false);
    // Solo al abrir / nueva sesión: los cambios posteriores son el dirty check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, formSession]);

  const isFormDirty = useMemo(
    () => JSON.stringify({ draft, variantDrafts }) !== baselineKey,
    [draft, variantDrafts, baselineKey],
  );

  const markFieldTouched = useCallback((field: string) => {
    setTouchedFields((current) => (current[field] ? current : { ...current, [field]: true }));
  }, []);

  const handleNameChange = useCallback(
    (value: string) => { markFieldTouched('name'); onNameChange(value); },
    [markFieldTouched, onNameChange],
  );
  const handleBarcodeChange = useCallback(
    (value: string) => { markFieldTouched('barcode'); onBarcodeChange(value); },
    [markFieldTouched, onBarcodeChange],
  );
  const handleSkuChange = useCallback(
    (value: string) => { markFieldTouched('sku'); onSkuChange(value); },
    [markFieldTouched, onSkuChange],
  );
  const handlePriceChange = useCallback(
    (value: string) => { markFieldTouched('price'); onPriceChange(value); },
    [markFieldTouched, onPriceChange],
  );
  const handleCategoryChange = useCallback(
    (value: Product['categoryId']) => { markFieldTouched('categoryId'); onCategoryChange(value); },
    [markFieldTouched, onCategoryChange],
  );

  // Errores de campo solo tras tocar el campo o intentar guardar — nunca al abrir.
  const showSkuError = saveAttempted || !!touchedFields.sku;
  const showBarcodeError = saveAttempted || !!touchedFields.barcode;
  const showPriceError = saveAttempted || !!touchedFields.price;
  const showDraftBanner = saveAttempted;

  // C5: Calculate stock that will be lost when converting simple→variants.
  // The server recalculates parent stock as SUM(variant cells). If the variant
  // cells don't sum to the parent stock, the difference is silently lost.
  const variantStockSum = useMemo(() => {
    return variantDrafts.reduce((sum, vd) => {
      const qty = parseDecimalInput(vd.stockQty);
      return sum + (qty ?? 0);
    }, 0);
  }, [variantDrafts]);

  const stockLossAmount = useMemo(() => {
    if (hideStockPanels || parentStockQty == null || parentStockQty <= 0) return 0;
    if (variantDrafts.length === 0) return 0;
    const loss = parentStockQty - variantStockSum;
    return loss > 0 ? loss : 0;
  }, [hideStockPanels, parentStockQty, variantDrafts.length, variantStockSum]);

  const guardSaveWithStockLossCheck = useCallback(
    async (originalSave: () => void) => {
      if (stockLossAmount > 0) {
        const ok = await confirmStockLoss({
          title: 'Stock que se va a perder',
          message: `El producto tiene ${Math.round(parentStockQty!)} unidades en stock. Las variantes suman ${Math.round(variantStockSum)} unidades. Al guardar, se perderán ${Math.round(stockLossAmount)} unidades del stock padre. ¿Querés continuar?`,
          confirmLabel: 'Guardar de todas formas',
          destructive: true,
        });
        if (!ok) return;
      }
      originalSave();
    },
    [stockLossAmount, parentStockQty, variantStockSum, confirmStockLoss],
  );

  const guardedOnSave = useCallback(() => {
    void guardSaveWithStockLossCheck(onSave);
  }, [guardSaveWithStockLossCheck, onSave]);

  const guardedOnSaveAndContinue = useCallback(() => {
    if (onSaveAndContinue) {
      void guardSaveWithStockLossCheck(onSaveAndContinue);
    }
  }, [guardSaveWithStockLossCheck, onSaveAndContinue]);

  // Un solo naming con el CTA y el título del módulo: "Nuevo ítem" (servicios)
  // / "Nuevo producto" (retail). Edición usa el sustantivo del itemType.
  const itemNoun = catalogItemNoun(draft.itemType ?? editingProduct?.itemType);
  const chromeCopy = catalogChromeCopy(serviceCatalogEnabled);
  const compactCreate = layoutWide && formCompact && formMode === 'create';
  const formTitle =
    formMode === 'edit'
      ? `Editar ${itemNoun.singular}`
      : formMode === 'duplicate'
        ? `Duplicar ${itemNoun.singular}`
        : chromeCopy.newItemLabel;
  const formSubtitle =
    formMode === 'duplicate' ? 'Copia' : formMode === 'create' ? chromeCopy.createModalSubtitle : undefined;
  const primaryActionLabel = saving ? 'Guardando...' : formMode === 'duplicate' ? 'Crear duplicado' : 'Guardar';
  const closeLocked = saving || !!imageUploading;
  const guardedClose = useCallback(() => {
    if (closeLocked) return;
    // Si el confirm de descarte ya está arriba, Escape/back lo cierra a él.
    if (discardConfirmOpen) return;
    if (!isFormDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: 'Descartar cambios',
      message: 'Hay cambios sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  }, [closeLocked, discardConfirmOpen, isFormDirty, onClose, confirmDiscard]);

  const brandPickerOptions = useMemo(
    () => [{ value: '', label: 'Elegir…' }, ...brandOptions.map((b) => ({ value: b.id, label: b.name }))],
    [brandOptions]
  );

  const supplierPickerOptions = useMemo(
    () => [{ value: '', label: 'Elegir…' }, ...supplierOptions.map((s) => ({ value: s.id, label: s.name }))],
    [supplierOptions]
  );

  const categorySheetOptions = useMemo(
    () => categoryOptions.filter((c) => c.id !== 'all').map((c) => ({ id: c.id, name: c.label })),
    [categoryOptions]
  );

  useEffect(() => {
    if (!visible) {
      setCategorySelectorVisible(false);
      setBrandPickerOpen(false);
      setSupplierPickerOpen(false);
      setUomPickerOpen(false);
    } else {
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ y: 0, animated: false });
      });
      // Foco en el campo principal al abrir — la animación del modal va primero.
      const focusTimer = setTimeout(() => {
        const focusBarcode =
          Platform.OS === 'web' &&
          layoutWide &&
          formCompact &&
          formMode === 'create' &&
          autoFocusBarcodeOnWeb;
        if (focusBarcode) barcodeInputRef.current?.focus();
        else nameInputRef.current?.focus();
      }, 350);
      return () => clearTimeout(focusTimer);
    }
  }, [visible, layoutWide, formCompact, formMode, autoFocusBarcodeOnWeb]);

  const formFieldsElement = (
    // key por sesión: al abrir / "guardar y agregar otro" el rail vuelve a la
    // primera sección y se resetea el estado local de los campos.
    <CatalogProductFormFields
      key={formSession}
      formMode={formMode}
      formSuggestion={formSuggestion}
      draft={draft}
      onNameChange={handleNameChange}
      onBarcodeChange={handleBarcodeChange}
      onSkuChange={handleSkuChange}
      onPriceChange={handlePriceChange}
      onItemTypeChange={onItemTypeChange}
      categoryOptions={categoryOptions}
      priceMarginPercentInput={priceMarginPercentInput}
      onPriceMarginPercentChange={onPriceMarginPercentChange}
      codeValidationMessage={showSkuError ? codeValidationMessage : null}
      barcodeValidationMessage={showBarcodeError ? barcodeValidationMessage : null}
      priceValidationMessage={showPriceError ? priceValidationMessage : null}
      draftValidationMessage={showDraftBanner ? draftValidationMessage : null}
      isDraftReady={isDraftReady || !showDraftBanner}
      formCompact={formCompact}
      onExpandForm={onExpandForm}
      currentBranchName={currentBranchName}
      currentBranchId={currentBranchId}
      editingProduct={editingProduct}
      editingProductStockLoading={editingProductStockLoading}
      editingProductStockFailed={editingProductStockFailed}
      onRetryEditingProductStock={onRetryEditingProductStock}
      onComboCompositionSaved={onComboCompositionSaved}
      comboDraftComponents={comboDraftComponents}
      onComboDraftChange={onComboDraftChange}
      comboPickOrganizationId={comboPickOrganizationId}
      onRecipeSaved={onRecipeSaved}
      onRecipeProduce={onRecipeProduce}
      onRecipeDisassemble={onRecipeDisassemble}
      editingProductCost={editingProductCost}
      canEditCost={canEditCost}
      editingProductCurrentMarginValue={editingProductCurrentMarginValue}
      editingProductCurrentMarginPercent={editingProductCurrentMarginPercent}
      editingProductSuggestedPrice={editingProductSuggestedPrice}
      currencyFormatter={currencyFormatter}
      onOpenScanner={onOpenScanner}
      onApplySuggestedPrice={onApplySuggestedPrice}
      showBarcodeCameraButton={showBarcodeCameraButton}
      onBarcodeSubmitEditing={onBarcodeSubmitEditing}
      autoFocusBarcodeOnWeb={autoFocusBarcodeOnWeb}
      canCreateCategory={canCreateCategory}
      onCreateCategory={onCreateCategory}
      canCreateBrand={canCreateBrand}
      onCreateBrand={onCreateBrand}
      canCreateSupplier={canCreateSupplier}
      onCreateSupplier={onCreateSupplier}
      onOpenCategoryPicker={() => setCategorySelectorVisible(true)}
      onOpenBrandPicker={() => setBrandPickerOpen(true)}
      onOpenSupplierPicker={() => setSupplierPickerOpen(true)}
      onOpenUomPicker={() => setUomPickerOpen(true)}
      supplierOptions={supplierOptions}
      brandOptions={brandOptions}
      onBrandIdChange={onBrandIdChange}
      onInternalNotesChange={onInternalNotesChange}
      onPublicDescriptionChange={onPublicDescriptionChange}
      onMinOrderQtyChange={onMinOrderQtyChange}
      onMaxOrderQtyChange={onMaxOrderQtyChange}
      onIsPublicChange={onIsPublicChange}
      onMenuAvailableChange={onMenuAvailableChange}
      onVisibleOnMenuCartaChange={onVisibleOnMenuCartaChange}
      onActiveIngredientChange={onActiveIngredientChange}
      onUsageTagsChange={onUsageTagsChange}
      onAllergensChange={onAllergensChange}
      pharmacyFieldsEnabled={pharmacyFieldsEnabled}
      onPreferredSupplierIdChange={onPreferredSupplierIdChange}
      onSupplierItemCodeChange={onSupplierItemCodeChange}
      allowIvaRate={allowIvaRate}
      weightUomEnabled={weightUomEnabled}
      onIvaRateChange={onIvaRateChange}
      onUnitOfMeasureChange={onUnitOfMeasureChange}
      onDurationMinutesChange={onDurationMinutesChange}
      onBufferBeforeMinutesChange={onBufferBeforeMinutesChange}
      onBufferAfterMinutesChange={onBufferAfterMinutesChange}
      onBookingModeChange={onBookingModeChange}
      onBookingPaymentModeChange={onBookingPaymentModeChange}
      onBookingDepositAmountChange={onBookingDepositAmountChange}
      onBookingDepositPercentChange={onBookingDepositPercentChange}
      onEvaluationProductIdChange={onEvaluationProductIdChange}
      onKitchenDestinationChange={onKitchenDestinationChange}
      onPrepMinutesChange={onPrepMinutesChange}
      kitchenTicketsEnabled={kitchenTicketsEnabled}
      menuDietaryFiltersEnabled={menuDietaryFiltersEnabled}
      serviceProductOptions={serviceProductOptions}
      serviceProductOptionsStatus={serviceProductOptionsStatus}
      onRetryServiceProductOptions={onRetryServiceProductOptions}
      hideStockPanels={hideStockPanels}
      variantsEnabled={variantsEnabled}
      variantDrafts={variantDrafts}
      parentStockQty={parentStockQty}
      onAddVariantDraft={onAddVariantDraft}
      onUpdateVariantDraft={onUpdateVariantDraft}
      onRemoveVariantDraft={onRemoveVariantDraft}
      onGenerateVariantMatrix={onGenerateVariantMatrix}
      onPickVariantImage={onPickVariantImage}
      onRemoveVariantImage={onRemoveVariantImage}
      variantImageUploadingKey={variantImageUploadingKey}
      onPickAxisImage={onPickAxisImage}
      onRemoveAxisImage={onRemoveAxisImage}
      axisImageUploading={axisImageUploading}
      inactiveVariants={inactiveVariants}
      onReactivateVariant={onReactivateVariant}
      nameInputRef={nameInputRef}
      barcodeInputRef={barcodeInputRef}
      mobile={!layoutWide}
      imageUrl={imageUrl}
      onPickImage={onPickImage}
      onRemoveImage={onRemoveImage}
      onPickVideo={onPickVideo}
      onRemoveVideo={onRemoveVideo}
      videoUploading={videoUploading}
      videoUrl={videoUrl ?? undefined}
      imageUploading={imageUploading}
    />
  );

  const formActionsProps = {
    isDraftReady,
    saving,
    imageUploading: !!(imageUploading || videoUploading),
    primaryActionLabel,
    draftValidationMessage,
    onClose: guardedClose,
    onSave: guardedOnSave,
    onSaveAndContinue: guardedOnSaveAndContinue,
    showSaveAndContinue,
    compactMode: compactCreate,
    onExpandForm,
    // Marca el intento: los errores de campo/banner aparecen solo al guardar.
    onSaveAttemptWhenInvalid: onSaveAttemptWhenInvalid
      ? (message: string) => {
          setSaveAttempted(true);
          onSaveAttemptWhenInvalid(message);
        }
      : () => setSaveAttempted(true),
  };

  const pickers = (
    <>
      <MasterFilterSheetModal
        visible={categorySelectorVisible}
        title={draft.itemType === 'service' ? 'Categoría (servicio)' : 'Categoría'}
        options={categorySheetOptions}
        selectedId={draft.categoryId || null}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) handleCategoryChange(id);
        }}
        onClose={() => setCategorySelectorVisible(false)}
        footerClearLabel="Limpiar"
        onFooterClear={() => handleCategoryChange(null)}
        footerCreateLabel={canCreateCategory && onCreateCategory ? 'Ir a categorías' : undefined}
        onFooterCreate={
          canCreateCategory && onCreateCategory
            ? () => {
                setCategorySelectorVisible(false);
                onCreateCategory();
              }
            : undefined
        }
      />
      <ModalNestedPicker
        visible={brandPickerOpen}
        title="Elegir marca"
        options={brandPickerOptions}
        selectedValue={draft.brandId ?? ''}
        onSelect={(value) => onBrandIdChange?.(value)}
        onClose={() => setBrandPickerOpen(false)}
        createLabel={canCreateBrand && onCreateBrandInline ? 'Nueva marca' : undefined}
        createPlaceholder="Nombre de la marca"
        onCreateOption={
          canCreateBrand && onCreateBrandInline
            ? async (name) => {
                await onCreateBrandInline(name);
                setBrandPickerOpen(false);
              }
            : undefined
        }
      />
      <ModalNestedPicker
        visible={supplierPickerOpen}
        title="Elegir proveedor"
        options={supplierPickerOptions}
        selectedValue={draft.preferredSupplierId ?? ''}
        onSelect={(value) => onPreferredSupplierIdChange?.(value)}
        onClose={() => setSupplierPickerOpen(false)}
        createLabel={canCreateSupplier && onCreateSupplierInline ? 'Nuevo proveedor' : undefined}
        createPlaceholder="Nombre del proveedor"
        onCreateOption={
          canCreateSupplier && onCreateSupplierInline
            ? async (name) => {
                await onCreateSupplierInline(name);
                setSupplierPickerOpen(false);
              }
            : undefined
        }
      />
      <MasterFilterSheetModal
        visible={uomPickerOpen}
        title="Unidad de medida"
        options={
          (draft.itemType === 'service'
            ? UOM_OPTIONS.filter((opt) => opt.id === 'unit' || opt.id === 'hour')
            : weightUomEnabled
              ? UOM_OPTIONS
              : UOM_OPTIONS.filter((opt) => opt.id !== 'kg' && opt.id !== 'g')
          ).map((opt) => ({ id: opt.id, name: opt.label }))
        }
        selectedId={
          draft.itemType === 'service' &&
          normalizeUnitOfMeasure(draft.unitOfMeasure) !== 'unit' &&
          normalizeUnitOfMeasure(draft.unitOfMeasure) !== 'hour'
            ? 'unit'
            : normalizeUnitOfMeasure(draft.unitOfMeasure)
        }
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) onUnitOfMeasureChange?.(id as ProductDraft['unitOfMeasure']);
        }}
        onClose={() => setUomPickerOpen(false)}
      />
    </>
  );

  if (layoutWide) {
    // Compact (D) queda angosto para alta rápida; el form completo usa el
    // layout B: rail de secciones + panel exclusivo (1000px).
    const compactActive = compactCreate;
    const railMode = !compactActive;
    const modalCardMaxWidth = Math.min(compactActive ? 560 : 1000, winW * 0.94);
    const modalMaxHeight = winH * 0.88;
    const modalHeight = railMode ? Math.min(680, modalMaxHeight) : undefined;
    return (
      <>
        <Modal visible={visible} transparent animationType="fade" onRequestClose={guardedClose}>
          <View style={[posUi.modalBackdrop, styles.wideRoot, modalBackdropPadding]}>
            <View
              style={[
                ticketUi.dialogCard,
                styles.dialogCard,
                { maxWidth: modalCardMaxWidth, maxHeight: modalMaxHeight, height: modalHeight, width: '100%' },
              ]}
              testID="catalog.form.modal"
            >
              <TicketModalHeader
                kicker="Catálogo"
                title={formTitle}
                subtitle={formMode !== 'create' ? formSubtitle : undefined}
                onClose={guardedClose}
                closeDisabled={closeLocked}
              />
              {railMode ? (
                // Rail + panel exclusivo: el body lo arma CatalogProductFormFields
                // (rail fijo + scroll interno del panel). Sin scroll doble acá.
                <View style={styles.railBodyWrap}>{formFieldsElement}</View>
              ) : (
                <ModalAwareScrollView
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ref={scrollRef as any}
                  style={styles.modalFormScroll}
                  contentContainerStyle={styles.modalFormScrollContent}
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator={false}
                  bottomOffset={spacing.sm}
                >
                  <View ref={scrollContentRef} collapsable={false}>
                    {formFieldsElement}
                  </View>
                </ModalAwareScrollView>
              )}
              <CatalogProductFormActions {...formActionsProps} />
            </View>
          </View>
        </Modal>
        {pickers}
        {StockLossConfirmModal}
        {DiscardConfirmModal}
      </>
    );
  }

  return (
    <>
      <Modal visible={visible} transparent animationType="slide" onRequestClose={guardedClose}>
        <View style={ticketUi.sheetBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={guardedClose} />
          <View
            style={[ticketUi.sheetCard, styles.sheetFlex, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}
            testID="catalog.form.modal"
          >
            <View style={ticketUi.grabber} />
            <TicketModalHeader
              kicker="Catálogo"
              title={formTitle}
              subtitle={formSubtitle}
              onClose={guardedClose}
              closeDisabled={closeLocked}
              compact
            />
            <ModalAwareScrollView
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ref={scrollRef as any}
              style={styles.sheetScroll}
              contentContainerStyle={styles.sheetScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              bottomOffset={spacing.sm}
            >
              <View ref={scrollContentRef} collapsable={false}>
                {formFieldsElement}
              </View>
            </ModalAwareScrollView>
            <CatalogProductFormActions {...formActionsProps} mobile />
          </View>
        </View>
      </Modal>
      {pickers}
      {StockLossConfirmModal}
      {DiscardConfirmModal}
    </>
  );
};

const styles = StyleSheet.create({
  wideRoot: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogCard: {
    overflow: 'hidden',
    borderRadius: 16,
    shadowColor: ticketColors.ink,
    shadowOpacity: 0.16,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
  },
  modalFormScroll: {
    flexShrink: 1,
  },
  railBodyWrap: {
    flex: 1,
    minHeight: 0,
    flexShrink: 1,
  },
  modalFormScrollContent: {
    paddingHorizontal: 22,
    paddingTop: 20,
    paddingBottom: spacing.lg,
    gap: 0,
  },
  sheetFlex: {
    flex: 1,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    shadowColor: ticketColors.ink,
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: -8 },
    elevation: 8,
  },
  sheetScroll: {
    flex: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 18,
    paddingBottom: spacing.md,
    gap: 0,
  },
});
